import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PhoneCall, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { MaskedLine } from "../components/site/Reveal";
import { usePageMeta, API, PHONE, PHONE_TEL } from "../lib/seo";

const services = [
  "Pre-Employment Drug Test", "DOT Drug Test", "Random Pool Testing", "Post-Accident Testing",
  "Reasonable Suspicion Testing", "Return-to-Duty Testing", "Breath Alcohol Test",
  "Ink Card Fingerprinting", "Live Scan Fingerprinting", "Compliance Consulting", "Multiple Services",
];

const initial = {
  company_name: "", contact_name: "", job_title: "", email: "", phone: "",
  service_needed: "", num_employees: "", preferred_datetime: "", testing_location: "", notes: "",
};

export default function RequestScreening() {
  usePageMeta(
    "Request a Screening | VeraScreen Solutions — DC & Maryland",
    "Request drug testing, alcohol screening, or fingerprinting services from VeraScreen Solutions. A representative will contact you within one business day."
  );

  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_needed) return toast.error("Please select a service.");
    setSending(true);
    try {
      await axios.post(`${API}/screening-requests`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="request-screening-page">
      <section className="relative overflow-hidden bg-[#1A3A6B] py-24 md:py-28">
        <div className="grain-overlay" />
        <div className="hero-grid absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.1}>Request a Screening</MaskedLine>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
            Complete the form below and a VeraScreen representative will contact you within one
            business day to confirm your request and provide next steps.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <div data-testid="urgent-callout" className="flex items-start gap-4 border-2 border-[#B8962E] bg-[#B8962E]/10 p-6">
              <PhoneCall size={24} className="mt-0.5 shrink-0 text-[#B8962E]" strokeWidth={1.5} />
              <div>
                <p className="font-display font-bold text-[#1A3A6B]">
                  For post-accident or time-sensitive testing, call us directly. Do not submit a form — call immediately.
                </p>
                <a href={PHONE_TEL} className="mt-2 inline-block text-lg font-bold text-[#B8962E] hover:underline">{PHONE}</a>
              </div>
            </div>
          </motion.div>

          {submitted ? (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="mt-12">
              <div data-testid="screening-confirmation" className="border border-[#0E7B6B]/30 bg-[#E7F2F0] p-10 text-center">
                <CheckCircle2 size={48} className="mx-auto text-[#0E7B6B]" strokeWidth={1.5} />
                <h2 className="font-display mt-6 text-2xl font-extrabold text-[#1A3A6B]">Thank you.</h2>
                <p className="mt-4 text-sm leading-relaxed text-[#333333]/85">
                  A VeraScreen representative will contact you within one business day. For
                  DOT-regulated testing, please have your company testing policy and employee
                  information ready. For fingerprinting appointments, valid government-issued photo
                  ID is required.
                </p>
                <button data-testid="screening-new-request-btn" className="btn-gold mt-8" onClick={() => { setForm(initial); setSubmitted(false); }}>
                  Submit Another Request
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25, ease: [0.22, 1, 0.36, 1] }} className="mt-12">
              <form onSubmit={submit} data-testid="screening-form" className="border border-gray-200 bg-white p-8 shadow-sm md:p-10">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label className="vs-label">Company Name *</label>
                    <input required data-testid="screening-input-company" className="vs-input" value={form.company_name} onChange={set("company_name")} />
                  </div>
                  <div>
                    <label className="vs-label">Primary Contact Name *</label>
                    <input required data-testid="screening-input-contact" className="vs-input" value={form.contact_name} onChange={set("contact_name")} />
                  </div>
                  <div>
                    <label className="vs-label">Job Title</label>
                    <input data-testid="screening-input-title" className="vs-input" value={form.job_title} onChange={set("job_title")} />
                  </div>
                  <div>
                    <label className="vs-label">Email Address *</label>
                    <input required type="email" data-testid="screening-input-email" className="vs-input" value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className="vs-label">Phone Number *</label>
                    <input required type="tel" data-testid="screening-input-phone" className="vs-input" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <label className="vs-label">Service Needed *</label>
                    <select required data-testid="screening-select-service" className="vs-input" value={form.service_needed} onChange={set("service_needed")}>
                      <option value="">Select a service…</option>
                      {services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="vs-label">Number of Employees to be Tested</label>
                    <input data-testid="screening-input-employees" className="vs-input" value={form.num_employees} onChange={set("num_employees")} placeholder="e.g. 12" />
                  </div>
                  <div>
                    <label className="vs-label">Preferred Date and Time</label>
                    <input data-testid="screening-input-datetime" className="vs-input" value={form.preferred_datetime} onChange={set("preferred_datetime")} placeholder="e.g. June 15, morning" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="vs-label">Testing Location</label>
                    <select data-testid="screening-select-location" className="vs-input" value={form.testing_location} onChange={set("testing_location")}>
                      <option value="">Select a location…</option>
                      <option value="Our Site">Our Site</option>
                      <option value="Mobile / On-Site at Our Location">Mobile / On-Site at Our Location</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="vs-label">Additional Notes</label>
                    <textarea rows={4} data-testid="screening-input-notes" className="vs-input resize-none" value={form.notes} onChange={set("notes")} />
                  </div>
                </div>
                <button type="submit" disabled={sending} data-testid="screening-submit-btn" className="btn-gold mt-8 w-full disabled:opacity-60">
                  {sending ? "Submitting…" : "Submit Request"}
                </button>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </main>
  );
}
